import moment from 'moment';
import { LLM_MODEL_FAMILY_HEADS } from './models.js';
import { LlmModelLifecycleState, } from './types.js';
/**
 * A `YYYY-MM-DD` plain date is "past" once its UTC midnight is at or before now.
 * Parsed with `moment.utc` (the repo's plain-date convention) so the comparison
 * never shifts a day for clients west of UTC.
 */
function isPastPlainDate(date) {
    return moment.utc(date, 'YYYY-MM-DD', true).isSameOrBefore(moment.utc());
}
/**
 * Derives a model's lifecycle state. Mirrors flow-api's `computeLifecycleState`
 * (past the obsolete date → `Obsolete`, past the retired date → `Retired`,
 * otherwise `Active`) and adds the registry's `forceObsolete` override, which
 * forces `Obsolete` regardless of dates.
 */
export function getLlmModelLifecycleState(model) {
    const { retiredDate, obsoleteDate, forceObsolete } = model.lifecycle;
    if (forceObsolete === true || (obsoleteDate !== null && isPastPlainDate(obsoleteDate))) {
        return LlmModelLifecycleState.Obsolete;
    }
    if (retiredDate !== null && isPastPlainDate(retiredDate)) {
        return LlmModelLifecycleState.Retired;
    }
    return LlmModelLifecycleState.Active;
}
/** Returns the concrete successor configured for a model's lifecycle family. */
export function getLlmModelSuccessor(model) {
    const modelFamily = model.lifecycle.successorModelFamily;
    return {
        modelFamily,
        modelName: LLM_MODEL_FAMILY_HEADS[modelFamily],
    };
}
/**
 * Resolves the model used at execution time. Obsolete models move one hop to
 * their lifecycle successor; Active and Retired models pass through unchanged.
 */
export function resolveLlmModelForExecution(model) {
    if (getLlmModelLifecycleState(model) !== LlmModelLifecycleState.Obsolete) {
        return { executionModel: model.id, fellBackFromObsolete: false };
    }
    return {
        executionModel: getLlmModelSuccessor(model).modelName,
        fellBackFromObsolete: true,
    };
}
/**
 * Whether a model may be offered for selection: any state but `Obsolete` (which
 * covers both date-obsolete and `forceObsolete` models). `Retired` (still
 * callable) models remain offerable.
 */
export function isLlmModelOfferable(model) {
    return getLlmModelLifecycleState(model) !== LlmModelLifecycleState.Obsolete;
}
//# sourceMappingURL=lifecycle.js.map